import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Send, Trash2, Eye, MousePointerClick, Users } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import type { Newsletter } from "@shared/schema";

const newsletterFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  volume: z.string().optional(),
  subject: z.string().optional(),
  fromName: z.string().optional(),
  scope: z.enum(["house", "talent"]).default("house"),
  bodyText: z.string().optional(),
  audienceType: z.enum(["media", "audience"]).default("audience"),
});

type NewsletterFormValues = z.infer<typeof newsletterFormSchema>;

const statusVariant: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  draft: "secondary",
  scheduled: "outline",
  sent: "default",
  archived: "outline",
};

export default function MarketingNewsletterPage() {
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);

  const { data: items, isLoading } = useQuery<Newsletter[]>({
    queryKey: ["/api/marketing/newsletters"],
  });

  const form = useForm<NewsletterFormValues>({
    resolver: zodResolver(newsletterFormSchema),
    defaultValues: { title: "", scope: "house", audienceType: "audience" },
  });

  const createMutation = useMutation({
    mutationFn: async (values: NewsletterFormValues) => {
      const payload = {
        title: values.title,
        volume: values.volume,
        subject: values.subject || values.title,
        fromName: values.fromName,
        scope: values.scope,
        contentBlocks: values.bodyText
          ? [{ type: "text", props: { content: values.bodyText } }]
          : [],
        audienceFilter: { type: values.audienceType },
        status: "draft",
      };
      const res = await apiRequest("POST", "/api/marketing/newsletters", payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketing/newsletters"] });
      toast({ title: "Newsletter created" });
      setCreateOpen(false);
      form.reset({ title: "", scope: "house", audienceType: "audience" });
    },
    onError: (err: any) => {
      toast({ title: "Failed to create", description: err?.message, variant: "destructive" });
    },
  });

  const sendMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/marketing/newsletters/${id}/send`);
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketing/newsletters"] });
      toast({ title: `Sent to ${data.recipientCount} recipients` });
    },
    onError: (err: any) => {
      toast({ title: "Send failed", description: err?.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/marketing/newsletters/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketing/newsletters"] });
      toast({ title: "Newsletter deleted" });
    },
  });

  return (
    <div className="p-6 space-y-6" data-testid="page-marketing-newsletter">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-page-title">Newsletter</h1>
          <p className="text-sm text-muted-foreground">Issue-based publishing for fans and buyers.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-new-newsletter">
              <Plus className="w-4 h-4 mr-2" /> New Issue
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>New Newsletter Issue</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit((v) => createMutation.mutate(v))} className="space-y-4">
                <FormField control={form.control} name="title" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl><Input {...field} placeholder="On the Road — Spring 2026" data-testid="input-title" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="volume" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Volume / Issue</FormLabel>
                      <FormControl><Input {...field} placeholder="Vol 12 / Issue 3" data-testid="input-volume" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="fromName" render={({ field }) => (
                    <FormItem>
                      <FormLabel>From name</FormLabel>
                      <FormControl><Input {...field} data-testid="input-from-name" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <FormField control={form.control} name="subject" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email subject</FormLabel>
                    <FormControl><Input {...field} data-testid="input-subject" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="scope" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Scope</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl><SelectTrigger data-testid="select-scope"><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="house">House (workspace-wide)</SelectItem>
                          <SelectItem value="talent">Per talent</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="audienceType" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Audience</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl><SelectTrigger data-testid="select-audience"><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="audience">Fan audience</SelectItem>
                          <SelectItem value="media">Media list</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <FormField control={form.control} name="bodyText" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Body</FormLabel>
                    <FormControl><Textarea rows={8} {...field} placeholder="Write your issue..." data-testid="input-body" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <DialogFooter>
                  <Button type="submit" disabled={createMutation.isPending} data-testid="button-save-newsletter">
                    {createMutation.isPending ? "Saving..." : "Save Draft"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      ) : !items || items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground" data-testid="text-empty-state">
            No newsletters yet. Create your first issue to start publishing.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {items.map((n) => (
            <Card key={n.id} data-testid={`card-newsletter-${n.id}`}>
              <CardContent className="p-4 flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold" data-testid={`text-newsletter-title-${n.id}`}>{n.title}</span>
                    <Badge variant={statusVariant[n.status] || "secondary"}>{n.status}</Badge>
                    {n.volume && <Badge variant="outline">{n.volume}</Badge>}
                    <Badge variant="outline">{n.scope === "house" ? "House" : "Talent"}</Badge>
                  </div>
                  {n.subject && <p className="text-sm text-muted-foreground mt-1">Subject: {n.subject}</p>}
                  <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {n.recipientCount} recipients</span>
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {n.openCount} opens</span>
                    <span className="flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> {n.clickCount} clicks</span>
                    {n.sentAt && <span>Sent {format(new Date(n.sentAt), "MMM d, yyyy")}</span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  {n.status !== "sent" && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => sendMutation.mutate(n.id)}
                      disabled={sendMutation.isPending}
                      data-testid={`button-send-${n.id}`}
                    >
                      <Send className="w-4 h-4 mr-1" /> Send
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => deleteMutation.mutate(n.id)} data-testid={`button-delete-${n.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
